
import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { LogIn, UserPlus, Globe, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useResponsiveSpacing, useResponsiveTouchTargets } from "@/lib/mobile-utils";
import { useNavbarState } from "./useNavbarState";

interface MenuItem {
  label: string;
  path: string;
}

interface NavbarMobileMenuProps {
  menuItems: MenuItem[];
  setIsMenuOpen: (open: boolean) => void;
  isMenuOpen: boolean;
}

const NavbarMobileMenu = ({ 
  menuItems,
  setIsMenuOpen,
  isMenuOpen
}: NavbarMobileMenuProps) => {
  const { isAuthenticated } = useAuth();
  const { language, handleLanguageChange } = useNavbarState();
  const { safeArea } = useResponsiveSpacing();
  const { buttonLarge, touchFeedback } = useResponsiveTouchTargets();
  const menuRef = useRef<HTMLDivElement>(null);
  const [startY, setStartY] = useState(0);
  
  const onNavigateToApp = () => {
    window.location.href = "/app";
  };

  const onClose = () => {
    setIsMenuOpen(false);
  };
  
  // Enhanced menu animation variants with mobile optimization
  const menuVariants = {
    hidden: { 
      opacity: 0,
      y: -20,
      scale: 0.95,
      transition: { 
        duration: 0.2,
        ease: "easeInOut"
      }
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: { 
        duration: 0.3,
        ease: "easeOut",
        staggerChildren: 0.05
      }
    },
    exit: { 
      opacity: 0, 
      y: -20,
      scale: 0.95,
      transition: { 
        duration: 0.2,
        ease: "easeInOut" 
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 }
  };
  
  // Enhanced swipe down to close functionality
  const handleTouchStart = (e: React.TouchEvent) => {
    setStartY(e.touches[0].clientY);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const endY = e.changedTouches[0].clientY;
    const diff = endY - startY;
    
    // If swiped down more than 100px, close the menu
    if (diff > 100) {
      onClose();
    }
  };

  // Focus management for accessibility
  useEffect(() => {
    if (isMenuOpen && menuRef.current) {
      const firstFocusable = menuRef.current.querySelector('button, [href]') as HTMLElement;
      firstFocusable?.focus();
    }
  }, [isMenuOpen]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  return (
    <AnimatePresence>
      {isMenuOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />
          
          {/* Menu Panel */}
          <motion.div 
            ref={menuRef}
            id="mobile-menu"
            className={`fixed inset-x-0 top-0 bg-white z-50 shadow-xl rounded-b-2xl ${safeArea}`}
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={menuVariants}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            role="dialog"
            aria-label="Menu de navegação móvel"
            aria-modal="true"
          >
            {/* Header with close button */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Menu</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className={`${touchFeedback} focus-visible-enhanced`}
                aria-label="Fechar menu"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            {/* Swipe indicator */}
            <div className="flex justify-center py-2">
              <div className="w-12 h-1 bg-gray-300 rounded-full" />
            </div>
            
            {/* Menu Content */}
            <div className="px-4 pb-6 space-y-3">
              {/* Language Toggle */}
              <motion.div variants={itemVariants}>
                <Button 
                  variant="outline" 
                  onClick={() => handleLanguageChange(language === "pt" ? "en" : "pt")} 
                  className={`w-full justify-start ${buttonLarge} ${touchFeedback} focus-visible-enhanced`}
                  aria-label={`Alterar idioma para ${language === "pt" ? "inglês" : "português"}`}
                >
                  <Globe className="h-5 w-5" />
                  <span className="ml-2">
                    {language === "pt" ? "English" : "Português"}
                  </span>
                </Button>
              </motion.div>
              
              {/* Authentication Buttons */}
              {isAuthenticated ? (
                <motion.div variants={itemVariants}>
                  <Button 
                    onClick={onNavigateToApp} 
                    className={`w-full ${buttonLarge} bg-brand-primary text-white hover:bg-brand-primary/90 ${touchFeedback} focus-visible-enhanced`}
                  >
                    <span>Meu Espaço</span>
                  </Button>
                </motion.div>
              ) : (
                <>
                  <motion.div variants={itemVariants}>
                    <Link to="/login" className="block" onClick={onClose}>
                      <Button 
                        variant="outline" 
                        className={`w-full justify-start ${buttonLarge} border-brand-primary text-brand-primary hover:bg-brand-primary/5 ${touchFeedback} focus-visible-enhanced`}
                      >
                        <LogIn className="h-5 w-5" />
                        <span className="ml-2">Entrar</span>
                      </Button>
                    </Link>
                  </motion.div>
                  
                  <motion.div variants={itemVariants}>
                    <Link to="/signup" className="block" onClick={onClose}>
                      <Button 
                        className={`w-full justify-start ${buttonLarge} bg-gradient-to-r from-brand-primary to-brand-secondary text-white hover:from-brand-secondary hover:to-brand-primary ${touchFeedback} focus-visible-enhanced`}
                      >
                        <UserPlus className="h-5 w-5" />
                        <span className="ml-2">Começar Gratuitamente</span>
                      </Button>
                    </Link>
                  </motion.div>
                </>
              )}
              
              {/* Additional Menu Items */}
              {menuItems.map((item, index) => (
                <motion.div key={item.path} variants={itemVariants}>
                  <Link to={item.path} className="block" onClick={onClose}>
                    <Button 
                      variant="ghost" 
                      className={`w-full justify-start ${buttonLarge} ${touchFeedback} focus-visible-enhanced`}
                    >
                      {item.label}
                    </Button>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default NavbarMobileMenu;
